import { useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, EffectFade } from 'swiper';
import { TimelineMax, TweenMax } from 'gsap';
import { useAnimate } from '../utils';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import EFLight from '@/components/UI/svg/LandingPage/EFLight';
import EFWifiRouter from '@/components/UI/svg/LandingPage/EFWifiRouter';
import EFPhone from '@/components/UI/svg/LandingPage/EFPhone';
import EFLaptop from '@/components/UI/svg/LandingPage/EFLaptop';
import EFRefrigerator from '@/components/UI/svg/LandingPage/EFRefrigerator';
import EFElectricHeater from '@/components/UI/svg/LandingPage/EFElectricHeater';
import EFMicroware from '@/components/UI/svg/LandingPage/EFMicroware';
import EFElectricKettle from '@/components/UI/svg/LandingPage/EFElectricKettle';
import EFCoffeeMaker from '@/components/UI/svg/LandingPage/EFCoffeeMaker';
import EFAirConditioner from '@/components/UI/svg/LandingPage/EFAirConditioner';
import { createEFPic } from '@/utils/useEFPic';

const { EFPic } = createEFPic('/img/landingPage/');
const BUY_LINK: any = {
    sa: 'https://www.amazon.ae/EF-ECOFLOW-Portable-Charging-Generator/dp/B0CC5PKRFQ/ref=sr_1_4?crid=1CS0UMRJ02XTQ&keywords=river+2+pro&qid=1690946106&sprefix=river+2+p%27ro%2Caps%2C269&sr=8-4',
    za: 'https://za.ecoflow.com/products/river-pro-portable-power-station',
    // au: 'https://au.ecoflow.com/collections/ecoflow-river-series/products/river-2-pro-portable-power-station',
};

export default function R2PTSB(): JSX.Element {
    const { useLang, Link, useRouter } = useGlobal();
    const { t } = useLang('landingPage');
    const { locale } = useRouter();

    const itemList = [
        {
            name: t('secF_Light'),
            times: `20 ${t('secF_hrs')}`,
            icon: <EFLight />,
        },
        {
            name: t('secF_WiFiRouter'),
            times: `23 ${t('secF_hrs')}`,
            icon: <EFWifiRouter />,
        },
        {
            name: t('secF_phone'),
            times: `67 ${t('secF_times')}`,
            icon: <EFPhone />,
        },
        {
            name: t('secF_laptop'),
            times: `12 ${t('secF_times')}`,
            icon: <EFLaptop />,
        },
        {
            name: t('secF_Refrigerator'),
            times: `5-10 ${t('secF_hrs')}`,
            icon: <EFRefrigerator />,
        },
        {
            name: t('secF_ElectricHeater'),
            times: `0.3 ${t('secF_hrs')}`,
            icon: <EFElectricHeater />,
        },
        {
            name: t('secF_Microwave'),
            times: `50 ${t('secF_min')}`,
            icon: <EFMicroware />,
        },
        {
            name: t('secF_ElectricKettle'),
            times: `50 ${t('secF_min')}`,
            icon: <EFElectricKettle />,
        },
        {
            name: t('secF_CoffeeMaker'),
            times: `0.7 ${t('secF_hrs')}`,
            icon: <EFCoffeeMaker />,
        },
        {
            name: t('secF_AirConditioner'),
            times: `0.4-0.8 ${t('secF_hrs')}`,
            icon: <EFAirConditioner />,
        },
    ];

    useEffect(() => {
        const { createScene, destroy, layoutH } = useAnimate();

        createScene({
            triggerElement: '.landing_page_R2PTSB',
            triggerHook: 1,
            offset: layoutH + 250,
        })
            .setClassToggle('.landing_page_R2PTSB', 'show')
            .addTo(aniController);

        createScene({
            triggerElement: '.landing_page_R2PTSB',
            triggerHook: 1,
            offset: 200,
            duration: (layoutH * 2) / 3,
        })
            .setTween(
                new TimelineMax().add(
                    TweenMax.to('.landing_page_F_wrapper', 1, {
                        opacity: 1,
                    }),
                ),
            )
            .addTo(aniController);

        return () => destroy();
    }, []);

    return (
        <div className={`landing_page_R2PTSB ${css.landing_page_R2PTSB}`}>
            <div className="landing_page_F_wrapper">
                <div className="page_F_left">
                    <div className="page_F_leftBox">
                        <div className="leftContentBox_1">
                            <div className="leftInnerBox">
                                <div className="secF_powerList">{t('secF_powerList')}</div>
                                <div className="leftListBox">
                                    {itemList.map((item: any, index: number) => (
                                        <div key={index} className="leftListItem">
                                            <div className="itemImg">{item.icon}</div>
                                            <div>
                                                <div className="itemName">{item.name}</div>
                                                <div className="itemTimes">{item.times}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="leftContentBox_2">
                            <div>
                                <div className="secF_title">{t('ts_bundle_river2_pro_1')}</div>
                                <div className="secF_pro_name">{t('ts_bundle_river2_pro_2')}</div>
                                <div className="secF_pro_paramBox">
                                    {t.rich('ts_bundle_river2_pro_3', {
                                        p: (text: string) => <p className="secF_pro_param">{`- ${text}`}</p>,
                                    })}
                                </div>
                                <div className="secF_pro_desc">{t('ts_bundle_river2_pro_4')}</div>
                                {BUY_LINK[locale] && (
                                    <Link
                                        className="secFButton btnHover"
                                        href={BUY_LINK[locale]}
                                        target="blank"
                                        dataLayer={{
                                            event: 'uaEvent',
                                            event_name: 'buy',
                                            product_name: t('ts_bundle_river2_pro_1'),
                                            position: `Solution Page_${t('ts_bundle_river2_pro_2')}`,
                                        }}
                                    >
                                        {locale === 'au' ? t('learn_more') : t('secF_buyNow')}
                                    </Link>
                                )}
                                <div className="secF_tip">{t('ts_bundle_river2_pro_tip')}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="page_F_right">
                    <Swiper
                        pagination={{ clickable: true }}
                        modules={[Pagination, Autoplay, EffectFade]}
                        effect="fade"
                        className="swiperBox"
                        autoplay={{
                            delay: 5000,
                        }}
                        loop
                    >
                        <SwiperSlide>
                            <EFPic
                                className="pageFImg"
                                name={[
                                    locale === 'za' ? 'bundle_river_pro.webp' : 'bundle_river2_pro.webp',
                                    locale === 'za' ? 'bundle_river_pro.jpg' : 'bundle_river2_pro.jpg',
                                ]}
                            />
                        </SwiperSlide>
                        <SwiperSlide>
                            <EFPic
                                className="pageFImg scale"
                                name={[
                                    locale === 'za'
                                        ? 'product/bundle_river_pro_ts.webp'
                                        : locale === 'au'
                                        ? 'product/R2+TS.webp'
                                        : 'product/bundle_river2_pro_ts.webp',
                                    locale === 'za'
                                        ? 'product/bundle_river_pro_ts.png'
                                        : locale === 'au'
                                        ? 'product/R2+TS.webp'
                                        : 'product/bundle_river2_pro_ts.png',
                                ]}
                            />
                        </SwiperSlide>
                    </Swiper>
                </div>
            </div>
        </div>
    );
}
